"use client";

import { useContext, useEffect, useState } from "react";
import Input from "ui/atoms/Input";
import shortHandToLoopSteps from "./shorthand/shorthandToLoopSteps";
import VexFlow from "./VexFlow/VexFlow";
import Slider from "rc-slider";
import { purple } from "tailwindcss/colors";
import Shape from "ui/atoms/Shape/Shape";
import useTapTempo from "util/useTapTempo";
import { volumeSliderProps } from "./usePlayer/useSyncSynthsWithExternalState";
import { useMountEffect } from "@react-hookz/web";
import trackPage from "shared/analytics/mixpanel/trackPage";
import Button from "ui/atoms/Button/Button";
import * as GameLayout from "ui/templates/Game/LayoutComponents";
import GameWrapper from "ui/templates/Game/GameWrapper";
import { Player } from "./usePlayer/usePlayer";
import {
  HandRaisedIcon,
  MusicalNoteIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/solid";
import times from "util/times";
import classNames from "classnames";
import { getChordByDegree, getChordTypeShortname } from "./constants/chords";
import { getScale, ScaleType } from "./constants/scales";
import {
  groupBasesMidiNoteWithSharps,
  midiToTonalNote,
  midiNotes as allMidiNotes,
} from "./constants/notes";
import ButtonPopover from "ui/atoms/ButtonPopover/ButtonPopover";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import PianoRange from "./PianoRange";
import CopyShortUrl from "./CopyShortUrlButton";
import { isNumber } from "lodash";
import Divider from "ui/atoms/Divider";
import { warmUpMelodies, WarmupMelody } from "./warmUpMelodies";
import * as HelpDialogues from "./HelpDialogues";
import chunk from "lodash.chunk";
import HorizontalScroller from "ui/atoms/HorizontalScroller";
import useAuth from "util/useAuth/useAuth";
import { intervalToDuration } from "date-fns";
import { isIndeterminateAuthState } from "util/useAuth/types";
import { pages } from "shared/analytics/mixpanel/constants";
import LockedOverlay from "ui/atoms/LockedOverlay";
import useVocalDriller from "./useVocalDriller";

import "rc-slider/assets/index.css";
import "@reach/tooltip/styles.css";
import VocalRegisterSelect from "./VocalRegisterSelect";

interface Props {}

export default function VocalDrillerClientPage({}: Props) {
  useMountEffect(() => {
    trackPage(pages.vocalDriller);
  });

  return (
    <HelpDialogues.HelpContextProvider>
      <GameContainer />
      <HelpDialogues.IntroModal />
    </HelpDialogues.HelpContextProvider>
  );
}

export function GameContainer() {
  const auth = useAuth();
  const [, setHelpContext] = useContext(HelpDialogues.HelpContext);
  const {
    url,
    timer,
    player,
    playButtonProps,
    playButtonTooltip,
    locked,
    recentlyUsedMelodyShortHands,
  } = useVocalDriller();

  return (
    <GameWrapper
      upgradeRedirectTo={url}
      auth={auth}
      inGameContextTitle={
        isIndeterminateAuthState(auth)
          ? "--"
          : `${formatSeconds(timer.seconds)}`
      }
    >
      <GameLayout.Toolbar mobileStyle={"fixed"}>
        <div className={"flex justify-between"}>
          <div
            className={"flex items-center overflow-auto mr-2 hide-scroll-bars"}
          >
            <div className={"mr-2"}>
              <Button
                theme={"default"}
                size="large"
                active={player.state.holdScale}
                onClick={player.toggleHoldScale}
                icon={(props) => <HandRaisedIcon {...props} />}
              />
            </div>

            <div className={"mr-2"}>
              <ButtonPopover
                buttonProps={{
                  theme: "default",
                  size: "large",
                  children: "BPM",
                }}
              >
                <div className={"text-white p-2"}>
                  <BPM player={player} />
                </div>
              </ButtonPopover>
            </div>

            <div className={"mr-2"}>
              <VocalRegisterSelect
                onChange={player.setVocalRegister}
                value={player.state.vocalRegister}
              />
            </div>

            <div className={"mr-2"}>
              <Button
                theme={"default"}
                size="large"
                icon={(props) => <QuestionMarkCircleIcon {...props} />}
                outline
                onClick={() => setHelpContext({ introModal: true })}
              />
            </div>
          </div>

          <div className={"flex items-center"}>
            <div>
              <Button
                {...playButtonProps}
                theme={"green"}
                size="large"
                icon={(props) => <PlayIcon {...props} />}
              />
            </div>
          </div>

          {playButtonTooltip}
        </div>
      </GameLayout.Toolbar>

      <GameLayout.Container>
        <PlaybackContainer
          player={player}
          recentlyUsedMelodyShortHands={recentlyUsedMelodyShortHands}
        />
      </GameLayout.Container>

      <LockedOverlay locked={locked} auth={auth} />
    </GameWrapper>
  );
}

interface PlaybackContainerProps {
  player: Player;
  recentlyUsedMelodyShortHands: string[];
}

function PlaybackContainer({
  player,
  recentlyUsedMelodyShortHands,
}: PlaybackContainerProps) {
  const [helpContext, setHelpContext] = useContext(HelpDialogues.HelpContext);

  /**
   * Playback state
   */
  const currentTransposition: string | null = player.state.activeScale
    ? midiToTonalNote(player.state.activeScale.rootMidi)
    : null;

  /**
   * Interval Builder
   */
  const [shorthandBlurred, setShorthandBlurred] = useState<boolean>(false);
  const shorthand = player.state.shorthands?.melody ?? "";
  const setShorthand = player.setMelodyShorthand;
  const [shorthandError, setShorthandError] = useState<string | null>(null);
  const shorthandSteps = shortHandToLoopSteps({
    melodyShorthand: shorthand ?? "",
  });

  useEffect(() => {
    if (!shorthandBlurred) {
      return;
    }

    const { error } = shortHandToLoopSteps({
      melodyShorthand: shorthand ?? "",
    });
    setShorthandError(error);
  }, [shorthand, shorthandBlurred]);

  /**
   *
   */

  const groupedSharpsWithRegularNotes =
    groupBasesMidiNoteWithSharps(allMidiNotes);

  return (
    <div>
      <div className={"mb-8 border-2 border-white rounded md:p-4 p-2"}>
        <div className={"mb-4"}>
          <h1 className={"md:flex justify-between items-center"}>
            <button
              className={
                "font-brik text-4xl leading-none transition-all group "
              }
              onClick={() =>
                setHelpContext({ playback: !helpContext?.playback })
              }
            >
              Playback
              <QuestionMarkCircleIcon
                className={
                  "w-8 ml-1 inline-block group-hover:scale-110 group-active:scale-90 transition-all"
                }
              />
            </button>

            {currentTransposition && (
              <div
                className={"text-purple-500 md:text-2xl leading-none font-bold"}
              >
                Current scale is {currentTransposition} {player.state.scaleType}
              </div>
            )}
          </h1>

          <HelpDialogues.Playback marginClassName="mt-2" />
        </div>

        <PlaybackContainerOverview player={player} />
      </div>

      <SectionDialogueWrapper
        title="Range"
        subtitle={
          player.state.range
            ? `${player.state.range.low}—${player.state.range.high}`
            : "Select to start"
        }
        needed={!player.state.range}
        className="md:mb-10 mb-6"
        helpKey={"range"}
      >
        <HelpDialogues.Range />

        <PianoRange midiNotes={groupedSharpsWithRegularNotes} player={player} />
      </SectionDialogueWrapper>

      <SectionDialogueWrapper
        title="Melody"
        subtitle={
          player.state.shorthands?.melody
            ? player.state.shorthands?.melody
            : "Select to start"
        }
        needed={!player.state.shorthands?.melody}
        className="md:mb-10 mb-6"
        helpKey={"melodyChords"}
      >
        <HelpDialogues.MelodyChords />

        <div>
          <MelodyShortHandTitle
            player={player}
            recentlyUsedMelodyShortHands={recentlyUsedMelodyShortHands}
            onChange={setShorthand}
          />

          <Input
            style="outline"
            state={shorthandError ? "error" : "default"}
            type="text"
            placeholder={"1 3 5---"}
            name="scale-selector"
            value={shorthand}
            icon={(props) => <MusicalNoteIcon {...props} />}
            onChange={(value) => {
              if (!shorthandBlurred) {
                setShorthandBlurred(true);
              }

              setShorthand(value);
            }}
            error={shorthandError}
          />
        </div>

        <div>
          <VexFlow
            steps={shorthandSteps.melodySteps}
            error={shorthandError !== null}
          />
        </div>

        <Divider className={"my-4"} />

        <div className={"font-brik mb-2"}>Some good starting places</div>

        <ul className={"overflow-auto whitespace-nowrap"}>
          <HorizontalScroller
            itemWidthClassName={""}
            count={chunk(warmUpMelodies, 2).length}
            render={(i) => {
              const subWarmUpMelodies: WarmupMelody[] = chunk(
                warmUpMelodies,
                2
              )[i];

              return (
                <div key={i} className="inline-grid gap-2">
                  {subWarmUpMelodies.map((warmUpMelody) => {
                    const selected =
                      shorthand === warmUpMelody.shorthand.melody;

                    return (
                      <li
                        key={warmUpMelody.title}
                        className={classNames(
                          "bg-black text-white",
                          selected
                            ? "border-purple-500"
                            : "border-white hover:bg-purple-500 hover:text-white hover:border-purple-500 hover:scale-[101%]",
                          "px-2 pt-1 pb-2 border rounded-sm cursor-pointer transition-all "
                        )}
                      >
                        <div
                          onClick={() => {
                            setShorthand(warmUpMelody.shorthand.melody);
                          }}
                        >
                          <div className={"text-xs font-brik"}>
                            {warmUpMelody.title}
                          </div>

                          <div
                            className={classNames(
                              "font-mono text-black px-2",
                              selected ? "bg-purple-500" : "bg-white"
                            )}
                          >
                            {warmUpMelody.shorthand.melody}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </div>
              );
            }}
          />
        </ul>
      </SectionDialogueWrapper>

      <SectionDialogueWrapper title="Share" helpKey={"share"}>
        <HelpDialogues.Share />

        <CopyShortUrl player={player} />
      </SectionDialogueWrapper>

      <div className={"h-96"}></div>
    </div>
  );
}

export function PlaybackContainerOverview({ player }: { player: Player }) {
  const chordRoot = new Set([
    Math.min(...Array.from(player.state.activeChordDegrees)),
  ]);

  return (
    <div className={"grid gap-1 md:pl-2 pl-1"}>
      <PlaybackContainerScaleNotes
        title="Scale Degrees"
        activeNoteDegrees={new Set()}
        scaleType={player.state.scaleType}
        minHeightClassName=""
        invert
        type="degree"
        rootMidi={player.state.activeScale?.rootMidi}
      />

      <PlaybackContainerScaleNotes
        title="Melody"
        activeNoteDegrees={player.state.activeMelodyDegrees}
        scaleType={player.state.scaleType}
        type="note"
        rootMidi={player.state.activeScale?.rootMidi}
      />

      <PlaybackContainerScaleNotes
        title="Chords"
        activeNoteDegrees={chordRoot}
        type="chord"
        scaleType={player.state.scaleType}
        rootMidi={player.state.activeScale?.rootMidi}
      />
    </div>
  );
}

function PlaybackContainerScaleNotes({
  title,
  activeNoteDegrees,
  type,
  scaleType,
  minHeightClassName = "min-h-[50px]",
  invert,
  rootMidi,
}: {
  rootMidi: number | undefined;
  title: string;
  activeNoteDegrees: Set<number>;
  type: "chord" | "degree" | "note";
  scaleType: ScaleType;
  minHeightClassName?: string;
  invert?: boolean;
}) {
  const scale = isNumber(rootMidi) ? getScale(rootMidi, scaleType) : null;

  return (
    <div className={"grid md:grid-cols-[75px_1fr] grid-cols-[50px_1fr]"}>
      <div
        className={
          "text-white font-bold flex items-center md:text-base text-xs md:leading-none leading-none"
        }
      >
        {title}
      </div>

      <div className={"grid grid-cols-8 gap-1 w-full"}>
        {times(8, (i) => {
          const degree = i + 1;
          const midiNote = scale ? scale.degreeToMidiNote.get(degree) : null;
          const displayNote = isNumber(midiNote)
            ? midiToTonalNote(midiNote)
            : "-";

          return (
            <div
              key={i}
              className={classNames(
                "px-1 py-2 text-center text-2xl font-brik flex items-center justify-center flex-col  border border-white",
                minHeightClassName,
                activeNoteDegrees.has(i + 1)
                  ? "bg-purple-500 text-white"
                  : invert
                  ? "bg-white text-black"
                  : "text-white bg-black"
              )}
            >
              <span className={"leading-none md:text-base text-sm"}>
                {type === "degree" ? degree : displayNote}
              </span>

              {type === "chord" && (
                <span className={"font-sans text-xs leading-none"}>
                  <span className={"hidden md:inline"}>
                    {getChordByDegree(scaleType, i + 1)}
                  </span>

                  <span className={"md:hidden"}>
                    {getChordTypeShortname(getChordByDegree(scaleType, i + 1))}
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface BPMProps {
  player: Player;
}

function BPM({ player }: BPMProps) {
  const minBpm = 10;
  const maxBpm = 350;
  const buttonIncrement = 5;
  const minMax = { min: minBpm, max: maxBpm };
  const metronomeSliderStyle = {
    track: { background: purple[500] },
    handle: {
      background: purple[500],
      borderColor: purple[500],
      boxShadow: "none",
    },
    rail: { background: "white" },
  };

  const bpm = player.state.bpm;
  const setBpm = player.setBpm;
  const tapTempo = useTapTempo(bpm, setBpm, minMax);
  const increment = () => {
    tapTempo.set(bpm + buttonIncrement);
  };

  const decrement = () => {
    tapTempo.set(bpm - buttonIncrement);
  };

  return (
    <div>
      <div>
        <div className={"text-center flex justify-center my-2"}>
          <input
            type="text"
            value={tapTempo.bpm}
            className="border-none inline-block bg-transparent text-white p-0 text-4xl w-20 text-center leading-none h-8 focus:outline-none focus:ring-0 focus:border-transparent"
            onChange={(e) => {
              const bpm = parseInt(e.target.value, 10);

              // if not valid number
              if (isNaN(bpm)) {
                return;
              }

              tapTempo.set(bpm);
            }}
          />
        </div>

        <div className={"w-full p-2 mb-2"}>
          <Shape
            type={"content"}
            theme={"white"}
            shape="circle"
            onClick={tapTempo.tap}
          >
            <div className={"font-brik leading-none"}>Tap</div>
          </Shape>
        </div>

        <div
          className={
            "text-white grid grid-cols-2 border border-white rounded h-5 bg-white gap-0.5 overflow-hidden text-xl font-brik leading-none"
          }
        >
          <button
            className={"h-full flex items-center justify-center bg-black"}
            onClick={decrement}
          >
            -
          </button>

          <button
            className={"flex items-center justify-center bg-black"}
            onClick={increment}
          >
            +
          </button>
        </div>
      </div>

      <hr className={"border-1 my-3 border-white"} />

      <div>
        <div className={"text-center text-xs"}>Metronome</div>

        <div className={"px-2"}>
          <Slider
            {...volumeSliderProps}
            styles={metronomeSliderStyle}
            value={player.state.volume.metronome}
            onChange={(value) => {
              const number = typeof value === "number" ? value : value[0];

              player.setVolume("metronome", number);
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface SectionDialogueWrapperProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  defaultOpen?: boolean;
  needed?: boolean;
  helpKey: keyof HelpDialogues.HelpContextState;
}

function SectionDialogueWrapper({
  title,
  subtitle,
  children,
  className,
  defaultOpen,
  needed,
  helpKey,
}: SectionDialogueWrapperProps) {
  const [helpContext, setHelpContext] = useContext(HelpDialogues.HelpContext);

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => {
        return (
          <div className={className}>
            <DisclosureButton
              className={classNames(
                "flex w-full items-center",
                needed ? "animate-pulse" : ""
              )}
            >
              <div className={"flex items-center flex-nowrap mr-2"}>
                <div
                  className={classNames(
                    needed ? "animate-wiggle animate-infinite" : ""
                  )}
                >
                  <Triangle
                    className={classNames(
                      "w-8 mr-2 transition-transform",
                      open ? "rotate-0" : "-rotate-90",
                      needed ? "fill-yellow-500" : "fill-white"
                    )}
                  />
                </div>

                <div
                  className={
                    "md:flex-row flex-col flex md:items-center items-start relative"
                  }
                >
                  <span
                    className={classNames(
                      "font-brik text-2xl leading-none",
                      needed ? "text-yellow-500" : "text-white"
                    )}
                  >
                    {title}
                  </span>

                  {subtitle && (
                    <span
                      className={classNames(
                        "md:relative absolute top-full text-base md:ml-2 font-bold leading-none whitespace-nowrap",
                        needed ? "text-yellow-500" : "text-purple-500 "
                      )}
                    >
                      ({subtitle})
                    </span>
                  )}
                </div>
              </div>

              <div
                className={classNames(
                  "md:h-4 h-2 w-full flex-1 rounded relative",
                  needed ? "bg-yellow-500" : "bg-white"
                )}
              >
                <div
                  tabIndex={-1}
                  role="button"
                  onClick={(e) => {
                    // Don't close when open, but open the dialogue when closed
                    if (open) {
                      e.stopPropagation();
                    }

                    if (!helpContext) {
                      setHelpContext({ [helpKey]: true });
                      return;
                    }

                    // when dialog is closed and it is opened by the ? make sure to also always open the dialog
                    setHelpContext({
                      [helpKey]: !open ? true : !helpContext[helpKey],
                    });
                  }}
                  className={
                    "whitespace-nowrap absolute -left-1  bg-black rounded-full top-1/2 -translate-y-1/2 transition-all hover:scale-110 active:scale-90 group"
                  }
                >
                  <QuestionMarkCircleIcon
                    className={classNames(
                      "w-8 inline-block group-hover:animate-wiggle-more group-hover:animate-infinite",
                      needed ? "fill-yellow-500" : "fill-white "
                    )}
                  />
                </div>
              </div>
            </DisclosureButton>

            <DisclosurePanel>
              <div
                className={
                  "border-2 border-white rounded-b border-t-0 md:pt-4 pt-6 md:p-4 p-2"
                }
              >
                {children}
              </div>
            </DisclosurePanel>
          </div>
        );
      }}
    </Disclosure>
  );
}

function Triangle({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 50 50" className={className}>
      <polygon points="0,0 50,0 25,50" />
    </svg>
  );
}

function formatSeconds(seconds: number) {
  // Calculate minutes and remaining seconds
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  // Create a duration object
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });

  // Format and return the duration
  // Custom formatting because formatDuration does not directly support MM:SS format
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function MelodyShortHandTitle({
  player,
  recentlyUsedMelodyShortHands,
  onChange,
}: {
  player: Player;
  recentlyUsedMelodyShortHands: string[];
  onChange: (shorthand: string) => void;
}) {
  if (recentlyUsedMelodyShortHands.length <= 0) {
    return <div className={"mr-2"}>Melody Shorthand</div>;
  }

  return (
    <div className={"flex whitespace-nowrap overflow-x-auto hide-scroll-bars"}>
      <div className={"mr-2"}>Recent Shorthands:</div>

      <ul className={""}>
        {recentlyUsedMelodyShortHands.map((shorthand, i) => (
          <li key={shorthand} className={"inline-flex items-center"}>
            <button
              onClick={() => onChange(shorthand)}
              className={classNames(
                "mr-2 hover:text-purple-500",
                shorthand === player.state.shorthands?.melody
                  ? "text-purple-500"
                  : "text-white"
              )}
            >
              {shorthand}
            </button>

            {recentlyUsedMelodyShortHands.length - 1 > i && (
              <div className={"mr-2"}>|</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
